import { CalendarIcon } from '@heroicons/react/24/solid';
import { forwardRef, useEffect, useRef, useState, useMemo } from 'react';
import type { BaseOptions } from 'flatpickr/dist/types/options';
import flatpickrCSS from 'flatpickr/dist/themes/light.css?inline';

import { Input, type InputProps } from './Input';
import { useLocaleContext } from '@lib/locale/contexts';
import { useMergedRef } from '@lib/hooks';
import { cn, cva, type VariantProps } from '@lib/utils';
import { injectStyles, insertStylesToHead, makeStyleTag } from '@lib/utils/dom/injectStylesToHead';

import { Flatpickr, type FlatpickrProps, type FlatpickrRef } from './Flatpickr';

const datepickerVariants = cva(
  [
    // Base
    'cursor-pointer',
  ],
  {
    variants: {
      isCalendar: {
        true: 'hidden',
        false: '',
      },
    },
    defaultVariants: {
      isCalendar: false,
    },
  }
);

type DatepickerProps = {
  options?: Partial<BaseOptions>;
  /** Render as inline calendar without input */
  isCalendar?: boolean;
  /** Show calendar icon prefix */
  hasCalendarIcon?: boolean;
  /**
   * @deprecated Use `hasCalendarIcon` instead
   */
  hasCalenderIcon?: boolean;
} & VariantProps<typeof datepickerVariants> &
  Omit<FlatpickrProps, 'options'> &
  Omit<InputProps, 'defaultValue' | 'value' | 'onChange' | 'prefix' | 'type'>;

const styles = `@layer vendor {
  ${flatpickrCSS}

  /* Hide native number input spinners - they desync with flatpickr's internal state */
  .flatpickr-time input.numInput::-webkit-inner-spin-button,
  .flatpickr-time input.numInput::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .flatpickr-time input.numInput[type=number] {
    -moz-appearance: textfield;
  }
}`;

const sheet = makeStyleTag();

injectStyles(sheet, styles);
insertStylesToHead(sheet);

const Datepicker = forwardRef<FlatpickrRef, DatepickerProps>(
  (
    {
      options: userOptions,
      className,
      isCalendar = false,
      hasCalendarIcon = true,
      hasCalenderIcon, // deprecated
      ...props
    },
    ref
  ) => {
    const flatpickrRef = useRef<FlatpickrRef | null>(null);
    const { locale } = useLocaleContext();
    const [localeData, setLocaleData] = useState<any>(null);

    // Support deprecated prop name
    const showCalendarIcon = hasCalenderIcon ?? hasCalendarIcon;

    useEffect(() => {
      const loadLocale = async () => {
        // For now, we'll use the default locale
        // Locale loading can be expanded when i18n is set up
        setLocaleData(null);
      };

      loadLocale();
    }, [locale]);

    const options = useMemo(() => {
      const opts: Partial<BaseOptions> = {
        inline: isCalendar,
        locale: localeData,
        ...userOptions,
      };

      // Time-only mode needs a default date for keyboard navigation to work properly.
      // Without a selected date, flatpickr's time manipulation breaks after a few changes.
      if (opts.noCalendar && opts.enableTime && !opts.defaultDate) {
        opts.defaultDate = new Date();
      }

      return opts;
    }, [isCalendar, localeData, userOptions]);

    const mergedRef = useMergedRef(flatpickrRef, ref);

    useEffect(() => {
      const calendarContainer = flatpickrRef.current?._flatpickr?.calendarContainer;
      if (calendarContainer) {
        calendarContainer.classList.toggle('is-calendar', isCalendar);
      }
    }, [isCalendar]);

    const renderComponent = useMemo(
      () => (inputProps: any, nodeRef: any) => {
        return isCalendar ? (
          <input ref={nodeRef} readOnly {...inputProps} />
        ) : (
          <Input
            ref={nodeRef}
            prefix={!userOptions?.inline && showCalendarIcon && <CalendarIcon className="size-5" />}
            readOnly
            {...inputProps}
          />
        );
      },
      [isCalendar, showCalendarIcon, userOptions?.inline]
    );

    return (
      <Flatpickr
        className={cn(datepickerVariants({ isCalendar }), className)}
        options={options}
        ref={mergedRef}
        {...props}
        render={renderComponent}
      />
    );
  }
);

Datepicker.displayName = 'Datepicker';

export { Datepicker };
export type { DatepickerProps };
